import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { ProgressBar } from '@/components/common/ProgressBar'
import {
  Alert,
  Box,
  Card,
  Chip,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material'
import { Group as GroupIcon, Person as PersonIcon } from '@mui/icons-material'

import { programAssignmentsApi, type AssignmentStatus, type ProgramAssignment } from '@/api/programs'
import { ROUTES } from '@/utils/constants'
import { getErrorMessage } from '@/utils/error'

/**
 * Запасные подписи статусов назначения.
 *
 * `provisioning` — не выдуманный статус: назначение остаётся в нём, если
 * запрос умер между резервированием слота и переводом в active (такие строки
 * подчищает команда `programs:cleanup-stuck-assignments`), а список их не
 * отсеивает. Без запасной подписи в таблице показывался бы сырой ключ.
 */
const STATUS_FALLBACK: Record<AssignmentStatus, string> = {
  provisioning: 'готовится',
  active: 'идёт',
  paused: 'на паузе',
  completed: 'завершена',
}

/**
 * Таблица назначенных программ — общий вход учителя к программе ученика.
 *
 * Один компонент на два места: вкладка «Назначения» в библиотеке программ и
 * раздел в карточке ученика. Разница только в фильтре, и держать две почти
 * одинаковые таблицы значило бы однажды получить два разных ответа на вопрос
 * «на чём остановился ученик».
 */
export function AssignmentsTable({
  studentId,
  dense = false,
  emptyText,
}: {
  /** Ограничить назначениями конкретного ученика — для его карточки */
  studentId?: number
  /** Компактный режим: без пагинации, только первые строки */
  dense?: boolean
  emptyText?: string
}) {
  const { t } = useTranslation()
  const navigate = useNavigate()

  // Колонка получателя нужна только в общем списке. В разрезе одного ученика
  // она отвечает на вопрос, который уже задан страницей.
  const showRecipient = studentId === undefined

  const [items, setItems] = useState<ProgramAssignment[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(0)
  const [perPage, setPerPage] = useState(dense ? 5 : 20)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      return await programAssignmentsApi.list({
        page: page + 1,
        per_page: perPage,
        ...(studentId === undefined ? {} : { student_id: studentId }),
      })
    } catch (err) {
      setError(getErrorMessage(err) || t('common.error'))

      return null
    } finally {
      setLoading(false)
    }
  }, [page, perPage, studentId, t])

  useEffect(() => {
    // Флаг отмены: ответ старого запроса не должен перетереть свежий список
    let cancelled = false

    void load().then((result) => {
      if (cancelled || result === null) return
      setItems(result.data)
      setTotal(result.meta?.total ?? result.data.length)
    })

    return () => {
      cancelled = true
    }
  }, [load])

  // Страница за пределами выдачи: назначения с неё завершили или перенесли.
  // Контрол пагинации на месте (он вне ветки с данными), но MUI на таком page
  // ругается и рисует пустой диапазон — возвращаемся на последнюю страницу сами
  useEffect(() => {
    if (dense || loading || page === 0 || page * perPage < total) return

    setPage(total === 0 ? 0 : Math.ceil(total / perPage) - 1)
  }, [dense, loading, page, perPage, total])

  // Колонок в шапке: получатель показывается только в общем списке. Число
  // нужно строкам-заглушкам — иначе они не растянутся на всю ширину таблицы
  const columnCount = showRecipient ? 5 : 4
  const skeletonRows = dense ? 3 : 5

  return (
    <TableContainer component={Card}>
      <Table size={dense ? 'small' : 'medium'}>
        <TableHead>
          <TableRow>
            {showRecipient && <TableCell>{t('programs.assignments.recipient')}</TableCell>}
            <TableCell>{t('programs.assignments.program')}</TableCell>
            <TableCell>{t('programs.assignments.currentTheme')}</TableCell>
            <TableCell sx={{ minWidth: 140 }}>{t('programs.assignments.progress')}</TableCell>
            <TableCell>{t('programs.assignments.status')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {/* Скелетон живёт строками внутри таблицы: подменять им весь блок
              значило снимать с экрана TablePagination, и учитель на каждой
              смене страницы терял единственный способ вернуться назад */}
          {loading &&
            Array.from({ length: skeletonRows }).map((_, rowIdx) => (
              <TableRow key={`skeleton-${rowIdx}`}>
                {Array.from({ length: columnCount }).map((__, colIdx) => (
                  <TableCell key={colIdx}>
                    <Skeleton variant="text" />
                  </TableCell>
                ))}
              </TableRow>
            ))}

          {!loading && error !== null && (
            <TableRow>
              <TableCell colSpan={columnCount}>
                <Alert severity="error">{error}</Alert>
              </TableCell>
            </TableRow>
          )}

          {/* Пустая выдача — тоже строка таблицы: со страницы, где назначений
              не осталось (их перенесли или завершили), иначе не выбраться */}
          {!loading && error === null && items.length === 0 && (
            <TableRow>
              <TableCell colSpan={columnCount}>
                <Typography color="text.secondary">
                  {emptyText ?? t('programs.assignments.empty')}
                </Typography>
              </TableCell>
            </TableRow>
          )}

          {!loading &&
            error === null &&
            items.map((assignment) => {
            const done = assignment.group_progress?.themes_done ?? 0
            const totalThemes = assignment.group_progress?.themes_total ?? 0
            const percent = totalThemes > 0 ? Math.round((done / totalThemes) * 100) : 0

            return (
              <TableRow
                key={assignment.id}
                hover
                sx={{ cursor: 'pointer' }}
                onClick={() =>
                  // В карточке ученика остаёмся в его разделе: общий адрес
                  // выбрасывал бы учителя из контекста, в котором он работает
                  navigate(
                    studentId === undefined
                      ? ROUTES.PROGRAM_ASSIGNMENT(assignment.id)
                      : ROUTES.USER_PROGRAM_ASSIGNMENT(studentId, assignment.id),
                  )
                }
              >
                {showRecipient && (
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {assignment.recipient?.type === 'group' ? (
                        <GroupIcon fontSize="small" color="action" />
                      ) : (
                        <PersonIcon fontSize="small" color="action" />
                      )}
                      {assignment.recipient?.name ?? '—'}
                    </Box>
                  </TableCell>
                )}
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {/* В разрезе ученика колонки получателя нет, но групповое
                        назначение от личного отличать всё равно нужно */}
                    {!showRecipient && assignment.recipient?.type === 'group' && (
                      <Tooltip title={assignment.recipient?.name ?? t('programs.group')}>
                        <GroupIcon fontSize="small" color="action" />
                      </Tooltip>
                    )}
                    {assignment.name}
                  </Box>
                </TableCell>
                <TableCell>
                  {/* Название темы закладки — ответ на «на чём остановились» */}
                  {assignment.bookmark?.theme_title ?? (
                    <Typography variant="body2" color="text.secondary">
                      {t('programs.assignments.notStarted')}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ProgressBar value={percent} height={6} sx={{ flex: 1 }} />
                    <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                      {done}/{totalThemes}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    variant={assignment.status === 'active' ? 'filled' : 'outlined'}
                    color={assignment.status === 'active' ? 'primary' : 'default'}
                    label={t(`programs.status.${assignment.status}`, STATUS_FALLBACK[assignment.status])}
                  />
                </TableCell>
              </TableRow>
            )
          })}
        </TableBody>
      </Table>

      {!dense && (
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={perPage}
          onRowsPerPageChange={(event) => {
            setPerPage(parseInt(event.target.value, 10))
            setPage(0)
          }}
          rowsPerPageOptions={[10, 20, 50]}
          labelRowsPerPage={t('programs.assignments.rowsPerPage')}
          labelDisplayedRows={({ from, to, count }) => `${from}–${to} ${t('common.of')} ${count}`}
        />
      )}
    </TableContainer>
  )
}
